import React, { useEffect, useState } from "react";
import { Input, FormFeedback, Tooltip, InputGroup, InputGroupText } from "reactstrap";

const PasswordInput = ({
    label = "Password",
    placeholder = "Enter your password",
    required = false,
    layout = "below",
    value = "",
    name = "password",
    id = "passwordInput",
    onChange = () => { },
    onBlur = () => { },
    requiredErrorMessage = "Password is required.",
    lengthErrorMessage = "Password must be between 6 and 20 characters.",
    complexityErrorMessage = "Password must include uppercase, lowercase, a number, and a special character.",
    showHelp = false,
    helpText = "Please enter a strong password.",
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [errors, setErrors] = useState({});
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Validation function for password input
    const validatePassword = (value) => {
        const newErrors = {};
        const hasUppercase = /[A-Z]/.test(value);
        const hasLowercase = /[a-z]/.test(value);
        const hasNumber = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);
        const isValidLength = value.length >= 8 && value.length <= 20;

        if (required && !value) {
            newErrors.required = requiredErrorMessage;
        }
        if (!isValidLength) {
            newErrors.length = lengthErrorMessage;
        }
        if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecialChar) {
            newErrors.complexity = complexityErrorMessage;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0; // Return true if no errors
    };

    // Handle input change
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        validatePassword(newValue);
        onChange(e);
    };

    // On blur, validate the input
    const handleInputBlur = (e) => {
        validatePassword(e.target.value);
        onBlur(e);
    };

    // Toggle password visibility
    const togglePasswordVisibility = () => {
        setShowPassword((prev) => !prev);
    };

    return (
        <div className={layout === "inline" ? "d-flex align-items-center" : ""}>
            <label htmlFor={id} className="d-flex" style={{ width: layout === "inline" ? "20%" : "100%" }}>
                {label}
                {required && <span className="text-danger ml-1">*</span>}
                {showHelp && (
                    <span id="helpTooltip" className="mx-2">
                        <i
                            className="ri-question-line"
                            style={{ cursor: 'pointer', fontSize: '16px' }}
                        ></i>
                    </span>
                )}
            </label>
            {showHelp && (
                <Tooltip
                    placement="right"
                    isOpen={tooltipOpen}
                    target="helpTooltip"
                    toggle={() => setTooltipOpen(!tooltipOpen)}
                >
                    {helpText}
                </Tooltip>
            )}
            <div className="w-100">
                <InputGroup>
                    <Input
                        type={showPassword ? "text" : "password"}
                        name={name}
                        id={id}
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={handleInputBlur}
                        invalid={!!errors.required || !!errors.length || !!errors.complexity}
                    />
                    <InputGroupText onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                        {showPassword ? "🙈" : "👁"}
                    </InputGroupText>

                    {errors.required && (
                        <FormFeedback type="invalid">
                            {errors.required}
                        </FormFeedback>
                    )}
                    {errors.length && (
                        <FormFeedback type="invalid">
                            {errors.length}
                        </FormFeedback>
                    )}
                    {errors.complexity && (
                        <FormFeedback type="invalid">
                            {errors.complexity}
                        </FormFeedback>
                    )}
                </InputGroup>
            </div>
        </div>
    );
};

export default PasswordInput;
